import React from 'react';
import { Tooltip, IconInfoLine, View, Text } from '@smarterservices/smarter-ui';

/**
 * Placement options for the tooltip
 */
type TooltipPlacement = 'top' | 'bottom' | 'start' | 'end' | 'top start' | 'top end' | 'bottom start' | 'bottom end';

/**
 * Size options for the icon
 */
type IconSize = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

/**
 * Color options for the icon
 */
type TextColor = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'alert' | 'brand';

/**
 * Props for the InfoIcon component
 */
interface InfoIconProps {
    /** The tooltip content to display */
    tip: string;
    /** Placement of the tooltip relative to the icon */
    placement?: TooltipPlacement;
    /** Maximum width of the tooltip */
    maxWidth?: string;
    /** Size of the info icon */
    size?: IconSize;
    /** Margin around the icon */
    margin?: string;
    /** Color of the icon */
    color?: TextColor;
    /** Additional props to pass to the Tooltip component */
    [key: string]: any;
}

/**
 * InfoIcon Component
 * 
 * A reusable info icon with tooltip that can be used to provide additional context
 * or help text throughout the application.
 * 
 * @component
 * @example
 * // Basic usage
 * <InfoIcon tip="This is helpful information" />
 * 
 * @example
 * // With custom width and placement
 * <InfoIcon 
 *   tip="This is a longer explanation that needs more space"
 *   maxWidth="400px"
 *   placement="bottom"
 * />
 * 
 * @example
 * // With custom icon size
 * <InfoIcon 
 *   tip="Small icon with info"
 *   size="small"
 * />
 * 
 * @example
 * // With custom margin
 * <InfoIcon 
 *   tip="Icon with custom spacing"
 *   margin="0 0 0 xx-small"
 * />
 * 
 * @example
 * // With custom color
 * <InfoIcon 
 *   tip="Secondary colored icon"
 *   color="secondary"
 * />
 * 
 * @example
 * // With both color and margin
 * <InfoIcon 
 *   tip="Styled icon with spacing"
 *   color="secondary"
 *   margin="0 0 0 small"
 * />
 */
function InfoIcon({
    tip,
    placement = 'top',
    maxWidth = '300px',
    size = 'x-small',
    margin,
    color,
    ...props
}: InfoIconProps) {
    const tipContent = <div style={{ maxWidth }}>{tip}</div>;
    const TooltipComponent = Tooltip as any;
    const IconComponent = IconInfoLine as any;
    const ViewComponent = View as any;
    const TextComponent = Text as any;

    const icon = <IconComponent size={size} />;

    // Wrap the entire Tooltip in styling wrappers to preserve event handlers
    let wrappedTooltip = (
        <TooltipComponent
            renderTip={tipContent}
            placement={placement}
            {...props}
        >
            {icon}
        </TooltipComponent>
    );

    // Wrap in Text if color is provided
    if (color) {
        wrappedTooltip = <TextComponent color={color}>{wrappedTooltip}</TextComponent>;
    }

    // Wrap in View if margin is provided
    if (margin) {
        wrappedTooltip = (
            <ViewComponent as="span" margin={margin} display="inline-block">
                {wrappedTooltip}
            </ViewComponent>
        );
    }

    return wrappedTooltip;
}

export default InfoIcon;
